


import {
  defineComponent,
  onMounted,
  onUnmounted,
  PropType,
  reactive,
  watch,
} from '@vue/composition-api';
import ReportModePane
  from '@/components/ExternalReports/HokkaidoAirport/components/ReportsView/ReportModePane/index.vue';
import MapMiscInfo from '@/components/ExternalReports/HokkaidoAirport/components/common/MapMiscInfo.vue';
import PhotoModal from '@/components/lib/PhotoModalWithEdit/index.vue';
import type { HokkaidoAirportMtxExt } from '@/models/externalReports/hokkaidoAirport/hokkaidoAirportMtx';
import type {
  HokkaidoAirportReportExt,
  HokkaidoAirportReportPhotoExt,
  HokkaidoAirportPhotoOrder,
} from '@/models/externalReports/hokkaidoAirport/hokkaidoAirportReport';
import ManualDownloadLinks
  from '@/components/ExternalReports/HokkaidoAirport/components/common/ManualDownloadLinks/index.vue';
import usePaneControl from '@/components/ExternalReports/HokkaidoAirport/composables/usePaneControl';
import { useHokkaidoAirportReports } from '@/components/ExternalReports/HokkaidoAirport/composables/useReports';
import { useExtremeMap } from '@/components/ExternalReports/HokkaidoAirport/composables/useExtremeMap';
import { usePhotoModal } from '@/components/ExternalReports/HokkaidoAirport/composables/usePhotoModal';
import {
  REPORT_LIST_MAX_HEIGHT_RATE,
  REPORT_LIST_MIN_HEIGHT_RATE,
} from '@/components/ExternalReports/HokkaidoAirport/consts';
import { requestPhotos, revokePhotosObjectURLs, fetchImageAsObjectURL } from '@/lib/imageHelper';
import { useRequestGuard } from '@/composables/useRequestGuard';
import type {
  HokkaidoAirportSettings,
} from '@/models/apis/externalReports/hokkaidoAirport/hokkaidoAirportSettings';
import { MAX_PENDING_PHOTOS_COUNT } from '@/consts/externalReports/hokkaidoAirport';

interface reportState {
  errorModalMsg: string;
  showErrorModal: boolean;
}
export default defineComponent({
  props: {
    userSettings: {
      type: Object as PropType<HokkaidoAirportSettings>,
      required: true,
    },
    isTrackVisible: {
      type: Boolean,
      required: true,
    },
  },
  emits: ['change-track-visibility'],
  setup(props, { emit }) {
    const state = reactive<reportState>({
      errorModalMsg: '',
      showErrorModal: false,
    });
    const {
      paneState,
      onChangePhotoTypeVisibility: onChangePhotoTypeVisibilityOrig,
    } = usePaneControl({
      paneStyleLimits: {
        listMinHeightRate: REPORT_LIST_MIN_HEIGHT_RATE,
        listMaxHeightRate: REPORT_LIST_MAX_HEIGHT_RATE,
      },
    });
    const {
      extremeMapRef,
      mapViewState,
      loadExtremeMapEssentials,
      forceResizeExtremeMap,
      showHokkaidoAirportReportLayer,
      removeHokkaidoAirportReportLayer,
      showHokkaidoAirportReportPhotoPopup,
      showHokkaidoAirportMtxPopup,
      hidePopup,
    } = useExtremeMap(paneState);
    const {
      reportState,
      searchReports,
      selectReport,
      deselectReport,
      getSelectedReport,
      getSelectedPhoto,
      getShowPhotos,
      deselectSelectedReportPhotos,
      resetSelectedPhotoSamePos,
      resetReportPhotosOrder,
      updateReportPhotosOrder,
      updateComment,
      deletePhoto,
      sendImageCandidate,
      updateReportPhotoSavedImages,
    } = useHokkaidoAirportReports();
    const { doRequest } = useRequestGuard((errorMessage) => {
      state.errorModalMsg = errorMessage;
      state.showErrorModal = true;
    });
    const {
      photoModalState,
      showPhotoModal: showPhotoModalOrig,
      hidePhotoModal,
      refreshPhotos,
    } = usePhotoModal();

    const showPhotoModal = async(photoType: string, photos: HokkaidoAirportReportPhotoExt[]) => {
      await doRequest(async() => {
        await requestPhotos(photos);
      }, '画像の取得に失敗しました。');
      updateReportPhotoSavedImages(photos);
      showPhotoModalOrig(photoType, photos);
    };
    const showPhotoModalFromButton = async(photoType: string, photos: HokkaidoAirportReportPhotoExt[]) => {
      reportState.saveLocation = null;
      await showPhotoModal(photoType, photos);
    };

    const showPhotoModalFromMap = async(photoType: string, photos: HokkaidoAirportReportPhotoExt[]) => {
      if (photos.length > 0) {
        reportState.saveLocation = {lat: photos[0].lat, lon: photos[0].lon};
      }

      await showPhotoModal(photoType, photos);
    };

    const redrawHokkaidoAirportReportLayer = (fitToExtent = false) => {
      const selectedReport = getSelectedReport();
      if (!selectedReport) return;

      showHokkaidoAirportReportLayer(selectedReport, fitToExtent, props.isTrackVisible);
    };

    const onChangePhotoTypeVisibility = (photoType: string, isVisible: boolean) => {
      onChangePhotoTypeVisibilityOrig(photoType, isVisible);
      redrawHokkaidoAirportReportLayer();
    };

    const isMtxSelected = (): boolean => {
      const selectedReport = getSelectedReport();
      if (!selectedReport) return false;

      return selectedReport.mtxsExt.some(mtx => mtx.isSelected);
    };

    const onChangeTrackVisibility = (isVisible: boolean): void => {
      emit('change-track-visibility', isVisible);
    };
    watch(() => props.isTrackVisible, (isVisible) => {
      // OFF で MTX点を消すため、MTX選択中ならポップアップを閉じて選択も外す（写真ピンは残るので写真選択中は閉じない）
      if (!isVisible && isMtxSelected()) {
        hidePopup();
        deselectSelectedReportPhotos();
      }
      redrawHokkaidoAirportReportLayer();
    });

    const onClickReport = async(selectedReport: HokkaidoAirportReportExt) => {
      hidePopup();
      // 選択中の報告書をクリックした場合、選択を解除する
      if (deselectReport(selectedReport.id)) {
        removeHokkaidoAirportReportLayer();
        return;
      }
      await doRequest(async() => {
        await selectReport(selectedReport.id);
      }, '報告書の取得に失敗しました。');
      redrawHokkaidoAirportReportLayer(true);
    };

    const onClickPhotoPin = async(photo: HokkaidoAirportReportPhotoExt) => {
      const isSelected = photo.isSelected;
      hidePopup();
      deselectSelectedReportPhotos();
      photo.isSelected = !isSelected;
      if (photo.isSelected) {
        // 画像を取得する
        if (!photo.savedImage && photo.imagePath) {
          await doRequest(async() => {
            photo.savedImage = await fetchImageAsObjectURL(photo.imagePath);
          }, '画像の取得に失敗しました。');
          if (!photo.savedImage) return;
        }
        resetSelectedPhotoSamePos();
        const selectedPhoto = getSelectedPhoto();
        if (!selectedPhoto) return;

        showHokkaidoAirportReportPhotoPopup(selectedPhoto);
      }
      redrawHokkaidoAirportReportLayer();
    };
    const onClickMtx = (mtx: HokkaidoAirportMtxExt) => {
      const isSelected = mtx.isSelected;
      deselectSelectedReportPhotos();
      hidePopup();
      mtx.isSelected = !isSelected;
      if (mtx.isSelected) {
        showHokkaidoAirportMtxPopup(mtx);
      }
      redrawHokkaidoAirportReportLayer();
    };
    const onAllDeselected = () => {
      hidePopup();
      deselectSelectedReportPhotos();
      redrawHokkaidoAirportReportLayer();
    };

    const paneResizeStopped = () => {
      forceResizeExtremeMap();
    };

    const addReportPhoto = async(file: File, photoType: string) => {
      await doRequest(async() => {
        await sendImageCandidate(file, photoType);
        await resetReportPhotosOrder();
      }, '写真の追加に失敗しました。');

      resetSelectedPhotoSamePos();
      refreshPhotoModel();
      refreshExtremeMapPhotos();
    };

    const updatePhotosOrder = async(photoId: number, newOrders: HokkaidoAirportPhotoOrder[]) => {
      await doRequest(async() => {
        await updateReportPhotosOrder(photoId, newOrders);
        await resetReportPhotosOrder();
      }, '写真の順序変更に失敗しました。');

      resetSelectedPhotoSamePos();
      refreshPhotoModel();
      refreshExtremeMapPhotos();
    };

    const deleteReportPhoto = async(photoId: number) => {
      await doRequest(async() => {
        await deletePhoto(photoId);
        await resetReportPhotosOrder();
      }, '写真の削除に失敗しました。');

      resetSelectedPhotoSamePos();
      refreshPhotoModel();
      refreshExtremeMapPhotos();
    };

    const updatePhotoComment = async(photoId: number, comment: string) => {
      await doRequest(async() => {
        await updateComment(photoId, comment);
      }, '写真のコメント更新に失敗しました。');

      resetSelectedPhotoSamePos();
      refreshPhotoModel();
      refreshExtremeMapPhotos();
    };

    const refreshPhotoModel = () => {
      const photos = getShowPhotos();
      if (!photos) return;

      refreshPhotos(photos);
    };

    const refreshExtremeMapPhotos = () => {
      redrawHokkaidoAirportReportLayer();
      if (!reportState.saveLocation) return;

      const photo = getSelectedPhoto();
      if (!photo) return;

      showHokkaidoAirportReportPhotoPopup(photo);
      hidePopup();
    };

    onMounted(async() => {
      await doRequest(async() => {
        await Promise.all([loadExtremeMapEssentials(), searchReports()]);
      }, '報告書の取得に失敗しました。');
    });

    onUnmounted(() => {
      revokePhotosObjectURLs(photoModalState.photos.map(p => p.savedImage));
    });

    return {
      state,
      mapViewState,
      paneState,
      photoModalState,
      // ref
      extremeMapRef,
      // consts
      MAX_PENDING_PHOTOS_COUNT,
      // methods
      deleteReportPhoto,
      hidePhotoModal,
      onClickReport,
      onClickPhotoPin,
      onClickMtx,
      onAllDeselected,
      onChangePhotoTypeVisibility,
      onChangeTrackVisibility,
      paneResizeStopped,
      showPhotoModalFromMap,
      showPhotoModalFromButton,
      addReportPhoto,
      updatePhotosOrder,
      updatePhotoComment,
    };
  },
  components: {
    ReportModePane,
    PhotoModal,
    ManualDownloadLinks,
    MapMiscInfo,
  },
});
